/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { excludedUsersUrl, portalUsersUrl } from "@Services/dataManager";
import AddRule from "./AddRule";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@Reducers/index";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { addUserToProject } from "@Actions/projectUsers";
import { getProjectRoles } from "@Actions/projectSetting";
import { getAllPositionUsersData } from "@Actions/orgStructure";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import _ from "lodash";
import type { ProjectRoleItem } from "@ServerTypes/projects/project.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

function AddUsersToProject({
  projectId,
  closeModal,
  selectUser,
  refreshAlreadyAddedUserData,
  customOnNext,
  customHeader = "Assign Users to Project",
  groupId,
  excludedUsers = [],
  source,
}: Readonly<{
  projectId: any;
  closeModal: any;
  selectUser: any;
  refreshAlreadyAddedUserData: any;
  customOnNext: any;
  customHeader?: any;
  groupId: any;
  excludedUsers: any;
  source?: "portalSetting" | "projectSetting";
}>) {
  const { t } = useTranslation();
  const dataGrid: any = useRef(null);
  const [assignRoleModalVisible, setAssignRoleModalVisible] = useState(false);
  const [addUsersModalVisible, setAddUsersModalVisible] = useState(true);
  const [nextEnabled, setNextEnabled] = useState(false);
  const [bulkSelectedRole, setBulkSelectedRole] = useState("");
  const [selectedRowData, setSelectedRowData] = useState([]);
  const [usersRoles, setUsersRoles]: any = useState({});
  const [activeTab] = useState("users");
  const [createRuleToggle] = useState(false);
  const [selectedUserDataForProfile, setSelectedUserDataForProfile]: any = useState("");
  const [ruleModalVisible, setRuleModalVisible] = useState(false);
  const [selectedOrgStructure, setSelectedOrgStructure] = useState([]);
  const [orgUsersList, setOrgUsersList] = useState([]);
  const [defaultRule, setDefaultRule] = useState("");

  const roleData = useSelector((state: RootState) => state.roleData) as ProjectRoleItem[];
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const dispatch = useDispatch();

  const query = useMemo(() => {
    const queryParams: {
      params: {
        projectId?: ObjectIdString;
        resourceType?: string;
        resourceId?: ObjectIdString;
        excludedUsers?: ObjectIdString[];
      };
      projectId?: ObjectIdString;
      where?: { field: string; operator: string; value: string | undefined }[];
    } = { params: {} };
    if (projectId) {
      queryParams.params.projectId = projectId;
      queryParams.projectId = projectId;
    } else if (groupId) {
      queryParams.params.resourceType = "RESOURCE_TYPE_GROUP";
      queryParams.params.resourceId = groupId;
    } else {
      queryParams.where = [{ field: "id", operator: "notequal", value: userDetails.id }];
      queryParams.params.excludedUsers = excludedUsers;
    }
    return queryParams;
  }, [projectId, groupId, userDetails.id, excludedUsers]);

  const backClickHandler = () => {
    handleBackClick();
    setAssignRoleModalVisible((prev) => !prev);
    setBulkSelectedRole("");
    setUsersRoles({});
  };

  useEffect(() => {
    if (projectId) {
      dispatch(getProjectRoles({ projectId }));
    }
  }, [projectId]);

  const AssignRoleSubmitHandler = async () => {
    if (projectId) {
      const saveData = Object.keys(usersRoles).map((user) => ({
        userId: user,
        roleId: usersRoles[user],
        resourceId: projectId,
        resourceType: "RESOURCE_TYPE_PROJECT",
      }));
      const res = await addUserToProject({ data: saveData, source });
      if (res.data.status) {
        dispatch(
          showToastNotification({
            content: t("Selected User(s) have been added to the project successfully!"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(res.data.errorMessage),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
      refreshAlreadyAddedUserData();
      closeModal();
    } else {
      const userDataWithRole: any = {};
      selectedRowData.forEach((data: any) => {
        if (Object.hasOwn(userDataWithRole, usersRoles[data.id])) {
          userDataWithRole[usersRoles[data.id]] = [
            ...userDataWithRole[usersRoles[data.id]],
            {
              userId: data.id,
              fullName: data.fullName,
              profilePic: data.profilePic,
            },
          ];
        } else {
          userDataWithRole[usersRoles[data.id]] = [
            {
              userId: data.id,
              fullName: data.fullName,
              profilePic: data.profilePic,
            },
          ];
        }
      });
      selectUser(userDataWithRole);
      closeModal();
    }
  };

  const handleCancelAddUsersModal = () => {
    closeModal();
  };

  const handleRowSelectionChange = () => {
    if (dataGrid.current) {
      const selectedItems = dataGrid.current?.dataView?.getAllSelectedItems() || [];
      if (selectedItems.length) {
        setNextEnabled(true);
      } else {
        setNextEnabled(false);
      }
    }
  };

  useEffect(() => {
    if (selectedOrgStructure.length) {
      setNextEnabled(true);
    } else {
      setNextEnabled(false);
    }
  }, [selectedOrgStructure]);

  const getUserAvatar = (id: any, userName: any, profilePic: any, avaterOnly?: any) => {
    return <UserNameTagComponent id={id} showAvatarOnly={avaterOnly} size="XL" isCircle />;
  };

  const userProfileOpenHandler = (user: any) => {
    setSelectedUserDataForProfile(user);
  };

  const handleCloseProfileModal = () => {
    setSelectedUserDataForProfile("");
  };

  const userProfileModalHeader = () => <div className="flex w-full justify-end"></div>;

  const userProfileModalContent = () => (
    <div className="flex flex-col items-center gap-2">
      {getUserAvatar(
        selectedUserDataForProfile.id,
        selectedUserDataForProfile.fullName,
        selectedUserDataForProfile.profilePic,
        true,
      )}
      <div className="text-base font-semibold text-[var(--primary-text)]">{selectedUserDataForProfile.fullName}</div>
      {selectedUserDataForProfile.designation && (
        <div className="text-sm text-[var(--primary-text)]">{selectedUserDataForProfile.designation}</div>
      )}
      <div className="text-sm text-[var(--primary-text)]">{selectedUserDataForProfile.userName}</div>
      {selectedUserDataForProfile.companyName && (
        <div className="text-sm text-[var(--primary-text)]">{selectedUserDataForProfile.companyName}</div>
      )}
      <div className="text-sm text-[var(--primary-text)]">{selectedUserDataForProfile.email}</div>
    </div>
  );

  const userProfileModal = () => (
    <ModalStateDialogComponent
      width="250"
      isVisible={selectedUserDataForProfile}
      headerTitle={userProfileModalHeader()}
      onCancel={handleCloseProfileModal}
      modelContent={userProfileModalContent()}
    />
  );

  const handleNextClick = async () => {
    let modalClose = true;
    if (activeTab === "users") {
      const selectedRecords = dataGrid.current?.dataView?.getAllSelectedItems() || [];
      setSelectedRowData(selectedRecords);
      customOnNext ? await customOnNext(selectedRecords) : setAssignRoleModalVisible(true);
    } else if (createRuleToggle) {
      setRuleModalVisible(true);
      const rules: any = [];
      selectedOrgStructure.forEach((ruleData: any) => {
        rules.push({
          field: ruleData.typeId,
          label: ruleData.type,
          type: "string",
          operator: "equal",
          value: ruleData.id,
        });
      });
      setDefaultRule(
        JSON.stringify({
          condition: "or",
          rules,
        }),
      );
    } else {
      const response = await getAllPositionUsersData({
        data: selectedOrgStructure.map((record: any) => record.id),
        orgId: userDetails.company?.id as string,
      });
      if (response.data.status) {
        if ((response.data.result as any[]).length) {
          const userList: any = [];
          (response.data.result as any[]).map((orgData: any) => {
            userList.push({
              id: orgData.heldBy,
              fullName: orgData.heldByName,
              profilePic: orgData.heldByProfilePic,
            });
          });
          setOrgUsersList(userList);
          customOnNext ? customOnNext(userList) : setAssignRoleModalVisible(true);
        } else {
          dispatch(
            showToastNotification({
              type: TOAST_TYPE_ERROR,
              content: t("Please select orgStructure that have minimun one heldBy position"),
            }),
          );
          modalClose = false;
        }
      }
    }
    if (modalClose) {
      setAddUsersModalVisible(false);
    }
  };

  const handleBackClick = () => {
    setRuleModalVisible(false);
    setAddUsersModalVisible(true);
    setSelectedOrgStructure([]);
  };

  const handleBulkRuleChange = (e: any) => {
    setBulkSelectedRole(e.value);
  };

  const setRoleForAllUsers = (userData: any) => {
    const allSelectedUserRole = { ...usersRoles };
    userData.map((data: any) => {
      allSelectedUserRole[data.id] = bulkSelectedRole;
    });
    setUsersRoles(allSelectedUserRole);
  };

  const setUserRole = (event: any, userId: any) => {
    const allUserRoles = { ...usersRoles };
    allUserRoles[userId] = event.value;
    setUsersRoles(allUserRoles);
  };

  const columnDefinitions = [
    {
      id: "customUserName",
      field: "customUserName",
      name: t("Emp ID"),
      minWidth: 100,
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
    },
    {
      id: "fullName",
      field: "fullName",
      name: t("User Name"),
      minWidth: 150,
      filterType: "Text",
      formatterType: "userTag",
      _valueGetter: (row: any) => row?.id,
      // Filter/search/options must work on the visible name, not the id _valueGetter feeds the avatar.
      _filterValueGetter: (row: any) => row?.fullName || row?.name || "",
      _excelValueGetter: (row: any) => row?.fullName || row?.name || row?.id || "",
    },
    {
      id: "designation",
      field: "designation",
      name: t("Designation"),
      minWidth: 120,
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
    },
    {
      id: "department",
      field: "department",
      name: t("Department"),
      minWidth: 120,
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
    },
    {
      id: "created",
      field: "created",
      name: t("Date Added"),
      minWidth: 120,
      formatterType: "Date",
      filterType: "Date",
    },
    {
      id: "companyName",
      field: "companyName",
      name: t("Org"),
      minWidth: 120,
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
    },
    {
      id: "role",
      field: "role",
      name: t("Portal Role"),
      minWidth: 120,
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
    },
    {
      id: "email",
      field: "email",
      name: t("Email"),
      minWidth: 150,
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
    },
  ];

  const addUserModalHeader = () => (
    <div className="flex w-full justify-between" data-testid="addUserModalHeader">
      <span className="text-[1.75rem] font-bold text-[var(--primary-text)]" data-testid="assignUsersToProject">
        {customHeader}
      </span>
    </div>
  );

  const GridDataList = useCallback(
    () => (
      <SlickGrid1
        ref={dataGrid}
        remoteDataSource={projectId ? excludedUsersUrl : portalUsersUrl}
        query={query}
        columnDefinitions={columnDefinitions as any}
        rowHeight={68}
        gridHeight="100%"
        onSelectedRowIdsChanged={handleRowSelectionChange}
        enableSearchBar
        enableFilteringOption
        enableSortingOption
        enableColumnChooser
        enableRowResize
        infiniteScroll
        data-testid="gridCompAssignUsersToProject"
      />
    ),

    [addUsersModalVisible],
  );

  const addUserModalContent = () => (
    <>
      <div>
        {!customOnNext && (
          <div className="flex justify-between p-[1.375rem]">
            <div className="flex items-center gap-5">
              <div className="flex h-[1.875rem] w-[1.875rem] items-center justify-center rounded-full bg-[var(--button-light)]">
                <span className="text-[var(--primary-text)]">1</span>
              </div>
              <div className="text-lg font-semibold text-[var(--primary-text)]" data-testid="adduserModalHeaderText">
                {t("Add users to this project from the list of available users")}
              </div>
            </div>
          </div>
        )}

        <div className="bg-[var(--system-colour-43)]">
          <div className="h-[25rem]">{GridDataList()}</div>
        </div>
      </div>
    </>
  );

  const assignRuleModalHeader = () => (
    <div className="flex w-full justify-between">
      <span className="text-[1.75rem] font-bold text-[var(--primary-text)]" data-testid="assignProjectRoleHeading">
        {t("Assign Project Role to selected users")}
      </span>
    </div>
  );

  const assignRuleModalContent = (userData: any) => (
    <div className="flex flex-col gap-5 pb-2">
      <div className="flex items-center gap-5 p-[1.375rem] pb-0 ps-10">
        <div className="flex h-[1.875rem] w-[1.875rem] items-center justify-center rounded-full bg-[var(--button-light)]">
          <span className="text-[var(--primary-text)]">2</span>
        </div>
        <div className="text-lg font-semibold text-[var(--primary-text)]" data-testid="selectSingleRoleToAll">
          {t("Select a single role for selected users")}
        </div>
      </div>

      <div className="flex h-20 items-center gap-10 border-px border-[var(--button-light)] ps-10">
        <div className="text-lg font-semibold text-[var(--primary-text)]" data-testid="role">
          {t("Role")}
        </div>
        <AutoComplete
          value={roleData.find((opt: any) => opt.id === bulkSelectedRole) || null}
          options={roleData}
          valueField="id"
          getOptionLabel={(option: any) => option?.frontendName || ""}
          onChange={(_event: any, newValue: any) => handleBulkRuleChange({ value: newValue?.id, itemData: newValue })}
          renderInput={(params: any) => <TextFieldComponent {...params} placeholder={t("Select Role")} />}
          sx={{ width: "8.75rem" }}
          data-testid="selectRoleInBulkUpdate"
        />
        <ButtonComponent
          buttonType={BUTTON_TYPES.BORDER_BUTTON}
          disabled={!bulkSelectedRole}
          onClick={() => setRoleForAllUsers(userData)}
          data-testid="bulkUpdateBtn"
        >
          {t("Bulk Update")}
        </ButtonComponent>
      </div>
      <div className="ps-10 text-lg font-semibold" data-testid="updateUsersIndividually">
        {t(".. or update the user roles individually")}
      </div>
      <div className="flex w-full flex-col gap-5 ps-10">
        <div className="flex w-full items-center gap-5">
          <span className="flex-1 cursor-pointer truncate text-base font-semibold" data-testid="userText">
            {t("User")}
          </span>
          <span className="flex-1 truncate text-base font-semibold" data-testid="roleText">
            {t("Role")}
          </span>
        </div>
        <div className="flex max-h-[14.375rem] flex-col gap-3 overflow-auto pe-2.5" data-testid="userAssignRoleDiv">
          {userData.map((userData: any, index: number) => (
            <div key={index} className="flex w-full items-center gap-5">
              <span
                className="flex-1 cursor-pointer truncate text-base font-semibold"
                onClick={() => {
                  userProfileOpenHandler(userData);
                }}
                data-testid="userProfileDetail"
              >
                {userData.fullName && getUserAvatar(userData.id, userData.fullName, userData.profilePic)}
              </span>
              <span className="flex-1 truncate text-base font-semibold" data-testid="selectRoleIndividually">
                <AutoComplete
                  value={roleData.find((opt: any) => opt.id === usersRoles[userData.id]) || null}
                  onChange={(_event: any, newValue: any) =>
                    setUserRole({ value: newValue?.id, itemData: newValue }, userData.id)
                  }
                  options={roleData}
                  valueField="id"
                  getOptionLabel={(option: any) => option?.frontendName || ""}
                  renderInput={(params: any) => <TextFieldComponent {...params} placeholder={t("Select Role")} />}
                />
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const addUserModal = () => (
    <ModalStateDialogComponent
      width="80%"
      allowModalPadding={false}
      headerTitle={addUserModalHeader()}
      modelContent={addUserModalContent()}
      onCancel={handleCancelAddUsersModal}
      footerContent={
        <div className="flex gap-[1.375rem]">
          <ButtonComponent
            buttonType={BUTTON_TYPES.BORDER_BUTTON}
            onClick={handleCancelAddUsersModal}
            data-testid="cancelAddUserBtn"
          >
            {t("Cancel")}
          </ButtonComponent>
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            onClick={handleNextClick}
            disabled={!nextEnabled}
            data-testid="nextAddUserBtn"
          >
            {" "}
            {t("Next")}{" "}
          </ButtonComponent>
        </div>
      }
      isVisible={addUsersModalVisible}
    />
  );

  const assignRuleModal = (userData: any) => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      width="60%"
      isVisible={assignRoleModalVisible}
      onCancel={handleCancelAddUsersModal}
      headerTitle={assignRuleModalHeader()}
      modelContent={assignRuleModalContent(userData)}
      footerContent={
        <div className="flex gap-[1.375rem]">
          <ButtonComponent
            buttonType={BUTTON_TYPES.BORDER_BUTTON}
            onClick={backClickHandler}
            data-testid="backBtnOfAssignRole"
          >
            {" "}
            {t("Back")}{" "}
          </ButtonComponent>
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            onClick={AssignRoleSubmitHandler}
            disabled={Object.keys(usersRoles).length != userData.length}
            data-testid="AddSelectedBtn"
          >
            {" "}
            {t("Add Selected")}{" "}
          </ButtonComponent>
        </div>
      }
    />
  );

  return (
    <div>
      {addUsersModalVisible && addUserModal()}
      {assignRoleModalVisible && assignRuleModal(activeTab === "users" ? selectedRowData : orgUsersList)}
      {selectedUserDataForProfile && userProfileModal()}
      {ruleModalVisible && (
        <AddRule closeModalPopup={closeModal} defaultRule={defaultRule} handleBackClick={handleBackClick} />
      )}
    </div>
  );
}

export default AddUsersToProject;
